import { type DocumentEdit } from '../../components/PhotoCapture/photoCaptureUtils';
import { isSome } from '../../utils';
import { toMap } from '../../utils/arrayUtils';
import { type FlightLegPhase } from '../FlightLegs/FlightLeg';
import { getDbSyncer } from '../LocalDB/DbSyncer';
import { getDb } from '../LocalDB/LocalDB';
import { type FlightDocument } from './FlightDocument';
import { isFlightDocumentSaved } from './FlightDocument';

export const saveFlightDocuments = async (documents: FlightDocument[]): Promise<void> => {
    const db = getDb();
    const documentsTable = db.documents;
    // One transaction: a sync reply landing between the read and the put would otherwise be overwritten.
    await db.transaction('rw', [documentsTable], async () => {
        await injectDocumentRemoteData(documents);
        await documentsTable.bulkPut(documents);
    });
    getDbSyncer().markToSync(documentsTable, ...documents.map((d) => d.id));
};
/**
 * Makes sure that if document was saved on the background, it will not have to be reuploaded again
 * @param documents
 */
const injectDocumentRemoteData = async (documents: FlightDocument[]) => {
    const ids = documents.map((d) => d.id);
    const existingDoc = (await getDb().documents.bulkGet(ids)).filter(isSome);
    const existingDocsById = toMap(existingDoc);
    documents.forEach((doc) => {
        const existing = existingDocsById.get(doc.id);
        if (existing) {
            doc._version = existing._version;
            doc.contentKey = existing.contentKey;
        }
    });
};

export const getFlightDocumentsForLeg = async (legId: string, phase: FlightLegPhase): Promise<FlightDocument[]> => {
    const allDocuments = await getDb().documents.where({ legId, phase }).toArray();

    return allDocuments.filter((doc) => !doc.squawkId);
};

export const getFlightDocumentsForFlight = async (flightId: string): Promise<FlightDocument[]> => {
    return getDb().documents.where({ flightId }).toArray();
};

export const getFlightDocumentsForFlightCount = async (flightId: string): Promise<number> => {
    return getDb().documents.where({ flightId }).count();
};

export const getFlightDocumentsForSquawk = async (squawkId: string): Promise<FlightDocument[]> => {
    return getDb().documents.where({ squawkId }).toArray();
};

export const getFlightDocumentsForRelease = async (releaseToServiceId: string): Promise<FlightDocument[]> => {
    const docs = await getDb().documents.where({ releaseToServiceId }).toArray();
    return loadFlightDocumentsWithSameMxRecord(docs);
};

async function loadFlightDocumentsWithSameMxRecord(documents: FlightDocument[]): Promise<FlightDocument[]> {
    const docsById = toMap(documents);
    const maintenanceRecordIds = documents.map((doc) => doc.maintenanceRecordId).filter(Boolean);
    const uniqueMXRIds = [...new Set(maintenanceRecordIds)];
    if (uniqueMXRIds.length) {
        await Promise.all(
            uniqueMXRIds.map(async (maintenanceRecordId) => {
                const docsForRelease = await getDb().documents.where({ maintenanceRecordId }).toArray();
                toMap(docsForRelease, docsById);
            })
        );
    }
    return [...docsById.values()];
}

// Onto the stored row, in one transaction: a caller's copy can be days old, and putting it would
// clobber contentKey and _version written by a sync in between, turning an update into a create.
export const saveFlightDocumentEdits = async (edits: DocumentEdit[]): Promise<void> => {
    if (!edits.length) {
        return;
    }
    const db = getDb();
    const documentsTable = db.documents;
    const updated = await db.transaction('rw', [documentsTable], async () => {
        const storedDocuments = await documentsTable.bulkGet(edits.map((edit) => edit.id));
        const toPut: FlightDocument[] = [];
        edits.forEach((edit, index) => {
            const stored = storedDocuments[index];
            if (!stored) {
                return;
            }
            const next = { ...stored };
            if (edit.title !== undefined) {
                next.title = edit.title;
            }
            if (edit.deleted !== undefined) {
                next.deleted = edit.deleted;
            }
            toPut.push(next);
        });
        if (toPut.length) {
            await documentsTable.bulkPut(toPut);
        }
        return toPut;
    });
    if (!updated.length) {
        return;
    }
    getDbSyncer().markToSync(documentsTable, ...updated.map((document) => document.id));
};

// The sync item must go with the row: syncItemToCommand throws on a missing row, which misaligns
// the rest of the batch.
export const purgeUnsyncedFlightDocument = async (documentId: string): Promise<void> => {
    const db = getDb();
    const flagged = await db.transaction('rw', [db.documents, db.localDocumentContents, db.syncItems], async () => {
        const stored = await db.documents.get(documentId);
        // A sync can have uploaded it since the caller read its copy; the server then needs the deletion.
        if (stored && isFlightDocumentSaved(stored)) {
            await Promise.all([
                db.documents.put({ ...stored, deleted: true }),
                db.localDocumentContents.delete(documentId),
            ]);

            return true;
        }
        await Promise.all([
            db.documents.delete(documentId),
            db.localDocumentContents.delete(documentId),
            db.syncItems.delete(`${db.documents.name}:${documentId}`),
        ]);

        return false;
    });
    if (flagged) {
        getDbSyncer().markToSync(db.documents, documentId);
    }
};
